import { type MouseEvent, useEffect, useState } from 'react';

import { keyframes } from '@emotion/react';
import { Avatar, Box, Button, Stack, Typography } from '@mui/material';
import { CalendarIcon } from 'lucide-react';

import { AppBannerContainer } from '@modules/_shared/components/AppBannerContainer';
import { useAppData } from '@modules/_shared/providers/AppDataProvider';
import { useLocale } from '@shared/hooks/locale';

declare global {
  interface Window {
    Calendly?: {
      initPopupWidget: (options: { url: string }) => void;
    };
  }
}

// ── Animations ────────────────────────────────────────────────────────────────

const ringAnim = keyframes`
  0%   { transform: scale(1);    opacity: 0.5; }
  100% { transform: scale(1.85); opacity: 0;   }
`;

const breatheAnim = keyframes`
  0%,100% { transform: translate(-50%,-50%) scale(0.85); opacity: 0.7; }
  50%     { transform: translate(-50%,-50%) scale(1.15);  opacity: 1;   }
`;

const blinkAnim = keyframes`
  0%,100% { opacity: 1;   }
  50%     { opacity: 0.3; }
`;

// ── Data ──────────────────────────────────────────────────────────────────────

type Advisor = {
  name: string;
  initials: string;
  calendlyUrl: string;
};

// All booking links go through the inbound demo router
// (cloudflare-workers/shortener.ts, `links.realadvisor.com/demo/<cc>`): it 302s to the
// country's INBOUND round-robin Calendly (the calendars the attribution flows
// classify as inbound) and passes the UTMs through, so an in-product demo request
// is credited to the product, not to an SDR. Add a country here only once it is
// in the router's table. Taxonomy: {cc}_crm_pro_agent_demo_banner (contract §4).
const DEFAULT_ADVISOR: Advisor = {
  name: 'RealAdvisor',
  initials: 'RA',
  calendlyUrl:
    'https://links.realadvisor.com/demo/ch?utm_source=crm&utm_medium=referral&utm_campaign=ch_crm_pro_agent_demo_banner',
};

const ADVISORS: Partial<Record<string, Advisor>> = {
  ch: {
    name: 'RealAdvisor',
    initials: 'RA',
    calendlyUrl:
      'https://links.realadvisor.com/demo/ch?utm_source=crm&utm_medium=referral&utm_campaign=ch_crm_pro_agent_demo_banner',
  },
  fr: {
    name: 'RealAdvisor',
    initials: 'RA',
    calendlyUrl:
      'https://links.realadvisor.com/demo/fr?utm_source=crm&utm_medium=referral&utm_campaign=fr_crm_pro_agent_demo_banner',
  },
  de: {
    name: 'RealAdvisor',
    initials: 'RA',
    calendlyUrl:
      'https://links.realadvisor.com/demo/ch?utm_source=crm&utm_medium=referral&utm_campaign=ch_crm_pro_agent_demo_banner', // German-speaking tenants book on the CH calendar
  },
  es: {
    name: 'RealAdvisor',
    initials: 'RA',
    calendlyUrl:
      'https://links.realadvisor.com/demo/es?utm_source=crm&utm_medium=referral&utm_campaign=es_crm_pro_agent_demo_banner',
  },
  it: {
    name: 'RealAdvisor',
    initials: 'RA',
    calendlyUrl:
      'https://links.realadvisor.com/demo/it?utm_source=crm&utm_medium=referral&utm_campaign=it_crm_pro_agent_demo_banner',
  },
  pl: {
    name: 'Tomasz',
    initials: 'TO',
    // Round-robin PL demo calendar via the router (was Tomasz's personal calendar,
    // which the PL attribution flow treats as outbound/SDR context).
    calendlyUrl:
      'https://links.realadvisor.com/demo/pl?utm_source=crm&utm_medium=referral&utm_campaign=pl_crm_pro_agent_demo_banner',
  },
  pt: {
    name: 'Joan',
    initials: 'JR',
    // PT books on the Cal.diy team round robin via the router (since 2026-10).
    calendlyUrl:
      'https://links.realadvisor.com/demo/pt?utm_source=crm&utm_medium=referral&utm_campaign=pt_crm_pro_agent_demo_banner',
  },
  gb: {
    name: 'RealAdvisor',
    initials: 'RA',
    calendlyUrl:
      'https://links.realadvisor.com/demo/ch?utm_source=crm&utm_medium=referral&utm_campaign=ch_crm_pro_agent_demo_banner',
  },
};

// ── Calendly script loader ────────────────────────────────────────────────────

const loadCalendly = () => {
  if (document.getElementById('calendly-css') == null) {
    const link = document.createElement('link');
    link.id = 'calendly-css';
    link.rel = 'stylesheet';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    document.head.appendChild(link);
  }
  if (document.getElementById('calendly-js') == null) {
    const script = document.createElement('script');
    script.id = 'calendly-js';
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);
  }
};

// ── Component ─────────────────────────────────────────────────────────────────

export const BookADemoBanner = () => {
  const { t, language } = useLocale();
  const { me } = useAppData();

  const countryCode = me?.tenant?.country_code?.toLowerCase();
  const advisor =
    (countryCode != null ? ADVISORS[countryCode] : undefined) ??
    DEFAULT_ADVISOR;
  // Every ADVISORS entry happens to carry UTMs today, but nothing enforces it:
  // a country added without a query string would produce `/demo/xx&lang=..`,
  // which DEMO_PATH does not match — the router falls through to the KV lookup
  // and 404s. Pick the separator instead of assuming one.
  const demoSep = advisor.calendlyUrl.includes('?') ? '&' : '?';
  const demoUrl = `${advisor.calendlyUrl}${demoSep}lang=${language}`;
  // Resolved calendly.com URL for the popup widget, fetched from the router ahead of
  // the click (`format=json`) so the click never depends on a round trip: if it isn't
  // resolved (router down, ad-blocked host, unknown country), the anchor simply
  // navigates and the router 302s to Calendly with the UTMs.
  const [popupUrl, setPopupUrl] = useState<string | null>(null);
  const isFreeTrial = me?.has_free_crm_access === true;
  const hasPremium = me?.subscriptions?.[0]?.status === 'active';
  // Only shown in PL for now (add 'pt' here when Portugal is ready), and never
  // to paying users (active subscription).
  const isAllowedCountry = countryCode === 'pl';
  // Never render a broken CTA: an entry with no booking link would give the
  // button an empty href (a reload of the current page) and open the Calendly
  // popup on an empty URL.
  const shouldShow =
    isFreeTrial &&
    !hasPremium &&
    isAllowedCountry &&
    advisor.calendlyUrl !== '';

  useEffect(() => {
    if (!shouldShow) {
      return;
    }
    loadCalendly();
    let cancelled = false;
    fetch(`${demoUrl}&format=json`, {
      headers: { accept: 'application/json' },
    })
      .then(res => (res.ok ? (res.json() as Promise<{ url?: string }>) : null))
      .then(data => {
        // The Calendly popup only renders calendly.com pages. Portugal books on
        // Cal.diy (meet.realadvisor.com): leave popupUrl unset so the click opens
        // the router link in the new tab the anchor already targets.
        if (
          !cancelled &&
          data?.url != null &&
          new URL(data.url).hostname === 'calendly.com'
        ) {
          setPopupUrl(data.url);
        }
      })
      .catch(() => {
        /* plain-link fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [shouldShow, demoUrl]);

  if (!shouldShow) {
    return null;
  }

  const userAvatarSrc = me?.user_images?.[0]?.image?.url ?? '';
  const userInitials = (me?.first_name?.[0] ?? '') + (me?.last_name?.[0] ?? '');

  const handleClick = (e: MouseEvent<HTMLAnchorElement>) => {
    const calendly = window.Calendly;
    if (calendly == null || popupUrl == null) {
      return; // let the anchor navigate: the router 302s to Calendly with the UTMs
    }
    e.preventDefault();
    calendly.initPopupWidget({ url: popupUrl });
  };

  return (
    <AppBannerContainer>
      {/* Card */}
      <Box
        sx={{
          position: 'relative',
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          alignItems: { xs: 'flex-start', md: 'flex-end' },
          flexWrap: { xs: 'nowrap', md: 'wrap' },
          gap: '20px',
          borderRadius: 2,
          padding: '22px 26px',
          background:
            'radial-gradient(120% 140% at 8% 50%, #dbeafe 0%, #eef4ff 40%, #ffffff 100%)',
          border: '1px solid rgba(82,120,223,0.15)',
          overflow: 'hidden',
        }}
      >
        {/* Left group: cluster + text */}
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'row',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '20px',
            flex: 1,
            minWidth: 0,
          }}
        >
          {/* Avatar cluster */}
          <Box
            sx={{
              position: 'relative',
              flexShrink: 0,
              display: 'flex',
              alignItems: 'center',
              pb: '4px',
            }}
          >
            {/* User avatar */}
            <Avatar
              src={userAvatarSrc}
              sx={{
                width: 58,
                height: 58,
                bgcolor: '#1d4ed8',
                fontSize: '1.1rem',
                fontWeight: 700,
                flexShrink: 0,
                mr: '-12px',
                zIndex: 1,
                boxShadow: '0 0 0 2px #fff, 0 6px 14px -4px rgba(15,23,42,.35)',
              }}
            >
              {userInitials}
            </Avatar>

            {/* Camera circle — centre, z-index 3 */}
            <Box
              sx={{
                position: 'relative',
                width: 54,
                height: 54,
                borderRadius: '50%',
                flexShrink: 0,
                zIndex: 3,
                display: 'grid',
                placeItems: 'center',
                bgcolor: '#fff',
                boxShadow:
                  '0 0 0 2.5px #fff, 0 6px 16px -4px rgba(37,99,235,.45)',
                // Animated rings via pseudo-elements
                '&::before, &::after': {
                  content: '""',
                  position: 'absolute',
                  inset: 0,
                  zIndex: -1,
                  borderRadius: '50%',
                  border: '2px solid #60a5fa',
                  animation: `${ringAnim} 2.2s ease-out infinite`,
                },
                '&::after': {
                  animationDelay: '1.1s',
                },
              }}
            >
              {/* Halo behind the circle */}
              <Box
                sx={{
                  position: 'absolute',
                  left: '50%',
                  top: '50%',
                  width: 74,
                  height: 74,
                  borderRadius: '50%',
                  background:
                    'radial-gradient(circle, rgba(37,99,235,.22), transparent 70%)',
                  zIndex: 0,
                  animation: `${breatheAnim} 2.6s ease-in-out infinite`,
                }}
              />

              {/* Video camera icon */}
              <Box
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  color: '#2563eb',
                  display: 'flex',
                }}
              >
                <svg
                  width="26"
                  height="26"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="m22 8-6 4 6 4V8Z" />
                  <rect x="2" y="6" width="14" height="12" rx="2" />
                </svg>
              </Box>

              {/* LIVE DEMO pill */}
              <Box
                sx={{
                  position: 'absolute',
                  bottom: '-9px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontSize: '10.5px',
                  fontWeight: 700,
                  letterSpacing: '.05em',
                  color: '#fff',
                  bgcolor: '#2563eb',
                  px: '9px',
                  py: '2px',
                  borderRadius: '3px',
                  whiteSpace: 'nowrap',
                  boxShadow: '0 2px 6px -1px rgba(37,99,235,.5)',
                  zIndex: 4,
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: '#fff',
                    animation: `${blinkAnim} 1.6s ease-in-out infinite`,
                  }}
                />
                LIVE DEMO
              </Box>
            </Box>

            {/* RealAdvisor logo avatar */}
            <Avatar
              src="/images/icons/icon-512x512.png"
              sx={{
                width: 58,
                height: 58,
                bgcolor: '#fff',
                flexShrink: 0,
                ml: '-12px',
                zIndex: 1,
                boxShadow: '0 0 0 2px #fff, 0 6px 14px -4px rgba(15,23,42,.35)',
              }}
            />
          </Box>

          {/* Text */}
          <Stack sx={{ flex: 1, minWidth: 200 }} spacing={0.5}>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: 600,
                fontSize: '1.125rem',
                letterSpacing: '-0.01em',
                lineHeight: 1.2,
              }}
            >
              {t('Get the most out of RealAdvisor')}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                fontWeight: 400,
                fontSize: '0.875rem',
                lineHeight: 1.45,
                color: '#475569',
              }}
            >
              {t('Schedule a free')}{' '}
              <Box component="span" sx={{ fontWeight: 700, color: '#334155' }}>
                {t('30-min demo')}
              </Box>{' '}
              {t(
                'with our team and discover all the features available to you.',
              )}
            </Typography>
          </Stack>
        </Box>
        {/* end left group */}

        {/* CTA button */}
        <Button
          component="a"
          href={demoUrl}
          target="_blank"
          rel="noopener noreferrer"
          onClick={handleClick}
          variant="contained"
          startIcon={<CalendarIcon />}
          sx={{
            flexShrink: 0,
            alignSelf: { xs: 'stretch', md: 'auto' },
            whiteSpace: 'nowrap',
            '& .MuiButton-startIcon': { transition: 'none' },
            '&:hover .MuiButton-startIcon': { transform: 'none' },
          }}
        >
          {t('Book a demo')}
        </Button>
      </Box>
    </AppBannerContainer>
  );
};
